import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { PayCommissionDefault } from '~hooks/usePayCommissionDefaults/models';
import usePayCommissionDefaults, {
  isUnprocessable,
} from '~hooks/usePayCommissionDefaults/usePayCommissionDefaults';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import CommissionDefaultForm, {
  CommissionDefaultFormHandler,
} from './CommissionDefaultForm';
import { CommissionDefaultDtoAsyncError } from './commissionDefaultSchema';

interface CommissionDefaultDialogProps {
  commissionDefault?: PayCommissionDefault;
  companyId: string;
  isDriver: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (commissionDefault: PayCommissionDefault) => void;
}

const CommissionDefaultDialog = observer(
  ({
    commissionDefault,
    companyId,
    isDriver,
    isOpen,
    onClose,
    onSuccess,
  }: CommissionDefaultDialogProps) => {
    const { toasterStore } = useStores();
    const { create, update } = usePayCommissionDefaults();
    const commissionDefaultFormRef = useRef<CommissionDefaultFormHandler>(null);
    const [asyncErrors, setAsyncErrors] = useState<CommissionDefaultDtoAsyncError[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const closeDialog = () => {
      setAsyncErrors([]);
      onClose();
    };

    const handleClose = () => {
      if (isSubmitting) {
        return;
      }

      closeDialog();
    };

    const handleSubmit = async () => {
      const formData = await commissionDefaultFormRef.current?.onSubmit();

      if (!formData) {
        return;
      }

      try {
        setAsyncErrors([]);
        setIsSubmitting(true);

        const saved = commissionDefault
          ? await update(companyId, commissionDefault.id, formData)
          : await create(companyId, formData, isDriver);

        toasterStore.push(
          alert(
            t(
              commissionDefault
                ? 'pay_commission_defaults.updated'
                : 'pay_commission_defaults.created',
              { name: saved.payeeName },
            ),
            AlertTypes.success,
          ),
        );
        onSuccess(saved);
        closeDialog();
      } catch (error) {
        if (!isUnprocessable(error)) {
          return;
        }

        const errorsArray = error.response?.data?.error?.errors ?? [];
        const fieldErrors = errorsArray.flatMap<CommissionDefaultDtoAsyncError>(
          ({ field, message }) => {
            if (['driver_id', 'vendor_account_id'].includes(field)) {
              return [
                {
                  field: 'rateType',
                  message: t('error_messages.pay_commission_defaults.duplicate'),
                },
              ];
            }

            const isPayeeError = ['driver', 'vendor', 'vendor_account'].includes(field);

            return isPayeeError ? [{ field: 'payee', message }] : [];
          },
        );

        if (fieldErrors.length) {
          setAsyncErrors(fieldErrors);
        } else {
          reportRequestError(
            error,
            t('error_messages.pay_commission_defaults.save_failed'),
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog isOpen={isOpen} preventCloseOnBackdropClick preventCloseOnEscapeKeyDown>
        <DialogHeader onClose={handleClose}>
          {commissionDefault
            ? t('pay_commission_defaults.update')
            : t('pay_commission_defaults.create')}
        </DialogHeader>

        <DialogContent>
          <CommissionDefaultForm
            ref={commissionDefaultFormRef}
            asyncErrors={asyncErrors}
            commissionDefault={commissionDefault}
            companyId={companyId}
            isDriver={isDriver}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(asyncErrors.length) && (
            <ShakyErrorIndicator message={t('pay_commission_defaults.invalid_data')} />
          )}

          <SmallButton
            color="secondary"
            disabled={isSubmitting}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton loading={isSubmitting} onClick={handleSubmit}>
            {commissionDefault ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CommissionDefaultDialog;
